import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { TABLE_ROWS_COUNT } from "../../shared/constants"; 
import { RootState } from "../../shared/types/common";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  FileTextOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import { Space, Button, Col, Row, Modal, Drawer } from "antd";
import { BiFilterAlt } from "react-icons/bi";
import DataTableComponent from "../../shared/components/data-table/data-table.component";
import GenericFormComponent from "../../shared/components/generic-form/generic-form.component";
import { useForm } from "antd/lib/form/Form";
import {
  deleteDocumentType,
  getDocumentTypeList,
} from "../../core/store/reducers/document-type.slice";
import { AnyAction, ThunkDispatch } from "@reduxjs/toolkit";
import { accessAllowed, showNotification } from "../../shared/utils/utils";
import ActivityLogComponent from "../../shared/components/activity-log/activity-log.component";
import { allPermissions } from "../../shared/constants/constants";
import { ActiveFilterChipsBar } from "../../shared/components/active-filter-chips/active-filter-chips.component";
import { isFilterValueActive } from "../../shared/utils/active-filters";
import CreateEditDocumentTypeComponent from "./components/create-edit-document-type.component";

const DOC_TYPE_FILTER_LABELS: Record<string, string> = {
  documentTypeName: "Name",
  status: "Status",
};

function docTypeFilterValueText(key: string, value: any): string {
  switch (key) {
    case "documentTypeName":
      return String(value).trim();
    case "status":
      return Array.isArray(value) ? value.join(", ") : String(value);
    default:
      return String(value);
  }
}

const DOC_TYPE_FILTER_CLEAR: Record<string, any> = {
  documentTypeName: undefined,
  status: [],
};

const ManageDocumentTypePage: React.FC = () => {
  const dispatch: ThunkDispatch<RootState, any, AnyAction> = useDispatch();
  const navigate = useNavigate();
  const [form] = useForm();
  const { loadingDocumentType, documentTypeList, pagination = { page: 1, total: 0 } } = useSelector(
    (state: RootState) => state.documentType || {}
  );
  const { myPermissions } = useSelector((state: RootState) => state.auth);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [documentTypeIdToDelete, setDocumentTypeIdToDelete] = useState("");
  const { screenType } = useSelector((state: RootState) => state.settings);

  // List local state
  const [documentTypeListData, setDocumentTypeListData] = useState([] as any);

  // Flags
  const [createEditOpen, setCreateEditOpen] = useState(false);
  const [selectedEntity, setSelectedEntity] = useState(null as any);
  const [filterVisible, setFilterVisible] = useState(false);

  // Actions and selections
  const [sorter, setSorter] = useState({ sort_by: "", sort_type: "" });
  const [filters, setFilters] = useState({} as any);
  const [openLogs, setOpenLogs] = useState(false);
  const [pageSize, setPageSize] = useState(TABLE_ROWS_COUNT);

  useEffect(() => {
    loadList(1);
  }, [filters, sorter, pageSize]);

  const loadList = (page: any = null) => {
    const data = {
      page: page ? page : pagination?.page ? pagination?.page : 1,
      limit: pageSize,
      ...sorter,
    };
    let filter = {} as any;
    Object.keys(filters)?.map((val: any) => {
      if (filters[val]) filter[val] = filters[val];
    });
    dispatch(getDocumentTypeList({ params: data, body: filter }));
  };

  const createEntityListner = () => {
    setCreateEditOpen(true);
    setSelectedEntity(null);
  };

  const editEntityListner = (entity: any) => {
    setCreateEditOpen(true);
    setSelectedEntity(entity);
  };

  useEffect(() => {
    setDocumentTypeListData(
      documentTypeList.map((obj: any, index: number) => {
        return { key: index, ...obj };
      })
    );
  }, [documentTypeList]);

  const onFilterApply = (values: any) => {
    setFilters(values);
  };

  const activeFilterEntries = useMemo(() => {
    return Object.keys(filters)
      .filter((k) => isFilterValueActive(filters[k]))
      .map((key) => ({
        key,
        fieldLabel: DOC_TYPE_FILTER_LABELS[key] || key,
        valueText: docTypeFilterValueText(key, filters[key]),
      }));
  }, [filters]);

  const clearAllFilters = useCallback(() => {
    setFilters({});
    form.resetFields();
  }, [form]);

  const removeFilter = useCallback(
    (key: string) => {
      setFilters((prev: any) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      const cleared =
        key in DOC_TYPE_FILTER_CLEAR ? DOC_TYPE_FILTER_CLEAR[key] : undefined;
      form.setFieldsValue({ [key]: cleared });
    },
    [form]
  );

  const onPageChange = (pageNumber: number) => {
    loadList(pageNumber);
  };

  const onPageSizeChange = (_pageNumber: number, selectedPageSize: number) => {
    setPageSize(selectedPageSize);
  };

  const hasAnyDocumentTypeAccess =
    accessAllowed(myPermissions, allPermissions.UPDATE_DOCUMENT_TYPE) ||
    accessAllowed(myPermissions, allPermissions.DELETE_DOCUMENT_TYPE) ||
    accessAllowed(myPermissions, allPermissions.DOCUMENT_TYPE_ACTIVITY_LOGS);

  const columns = [
    {
      title: "Document Type Name",
      dataIndex: "documentTypeName",
      sorter: false,
      key: "documentTypeName",
    },
    {
      title: "Status",
      dataIndex: "status",
      sorter: false,
      key: "status",
    },
    ...(hasAnyDocumentTypeAccess
      ? [
          {
            title: "Actions",
            key: "actions",
            fixed: "right",
            render: (text: any, record: any) => (
              <Space>
                {accessAllowed(
                  myPermissions,
                  allPermissions.UPDATE_DOCUMENT_TYPE
                ) && (
                  <Button
                    type="link"
                    onClick={() => editEntityListner(record)}
                    icon={<EditOutlined />}
                  />
                )}
                {accessAllowed(
                  myPermissions,
                  allPermissions.DELETE_DOCUMENT_TYPE
                ) && (
                  <Button
                    type="link"
                    onClick={() => handleDeleteModal(record?.id)}
                    icon={<DeleteOutlined style={{ color: "#FF0000" }} />}
                  />
                )}
                {accessAllowed(
                  myPermissions,
                  allPermissions.DOCUMENT_TYPE_ACTIVITY_LOGS
                ) && (
                  <Button
                    type="link"
                    onClick={() => handleOpenLogs(record)}
                    icon={<EyeOutlined />}
                  />
                )}
              </Space>
            ),
          },
        ]
      : []),
  ];

  const handleDelete = (id: string) => {
    dispatch(deleteDocumentType(id))
      .unwrap()
      .then(() => {
        showNotification("Document Type Deleted Successfully", "success");
        loadList();
        setDeleteModalVisible(false);
      })
      .catch(() => {
        // Error is handled by the slice, just close the modal
        setDeleteModalVisible(false);
      });
  };

  const handleOpenLogs = (record: any) => {
    setOpenLogs(true);
    setDocumentTypeIdToDelete(record?.id);
    setSelectedEntity(record);
  };

  const onCloseLogs = () => {
    setOpenLogs(false);
    setDocumentTypeIdToDelete("");
  };

  const handleDeleteModal = (id: string) => {
    setDeleteModalVisible(true);
    setDocumentTypeIdToDelete(id);
  };

  const onDeleteConfirm = () => {
    handleDelete(documentTypeIdToDelete);
  };

  const onDeleteCancel = () => {
    setDeleteModalVisible(false);
    setDocumentTypeIdToDelete("");
  };

  const onChangeHandler = (_pagination: any, _filters: any, _sorter: any) => {
    if (_sorter.order === undefined) {
      setSorter({ sort_by: "", sort_type: "" });
    } else {
      let order = "";
      let columnKey = "";
      if (_sorter.order === "ascend") order = "asc";
      else order = "desc";

      if (_sorter.columnKey === "enabled") {
        columnKey = "status";
      } else if (_sorter.columnKey === "displayName") {
        columnKey = "name";
      } else {
        columnKey = _sorter.columnKey;
      }
      setSorter({ sort_by: columnKey, sort_type: order });
    }
  };

  const createEditSubmit = () => {
    setCreateEditOpen(false);
    loadList();
  };

  useEffect(() => {
    loadList();
  }, []);

  const onValuesChange = (value: any, _allValues: any) => {};

  const formProps = {
    form: form,
    name: "manage-document-type-filters",
    enableEnterSubmit: true,
    formElements: [
      {
        name: "documentTypeName",
        label: "",
        placeholder: "Document Type Name Search",
        type: "Input",
        colSpanResponsive: { xs: 12, sm: 8, md: 6, lg: 4 },
      },
      {
        name: "status",
        label: "",
        placeholder: "Status",
        type: "Select",
        colSpanResponsive: { xs: 12, sm: 8, md: 6, lg: 4 },
        showSearch: true,
        allowClear: true,
        mode: "multiple",
        options: [
          { value: "ACTIVE", label: "ACTIVE" },
          { value: "INACTIVE", label: "INACTIVE" },
        ],
      },
    ],
    initialValues: {
      name: "",
    },
    extraButtons: [
      {
        htmlType: "submit",
        colSpanResponsive: { xs: 12, sm: 8, md: 6, lg: 3 },
        loading: false,
        submitButtonText: "Clear",
        type: "clearForm",
        onClick: clearAllFilters,
      },
      {
        htmlType: "submit",
        colSpanResponsive: { xs: 12, sm: 8, md: 6, lg: 3 },
        loading: false,
        submitButtonText: "Apply",
        submitButtonType: "primary",
      },
    ],
    onFinish: onFilterApply,
    onFinishFailed: null,
    onValuesChange: onValuesChange,
  };

  const headerButtons = (
    <Space size="middle" className="app-page-actions">
      {accessAllowed(myPermissions, allPermissions.CREATE_DOCUMENT_TYPE) && (
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={createEntityListner}
        >
          <span className="font-size-14">
            New {!screenType?.isPortrait && "Document Type"}
          </span>
        </Button>
      )}
      <Button
        shape="circle"
        type="primary"
        icon={<BiFilterAlt />}
        onClick={() => setFilterVisible(!filterVisible)}
        title={filterVisible ? "Hide filters" : "Show filters"}
      />
    </Space>
  );

  return (
    <div className="app-page">
      <div className="app-page-header">
        <Row align="middle" justify="space-between" wrap>
          <Col flex="1" style={{ minWidth: 0 }}>
            <div className="app-page-title-wrap">
              <div className="app-page-icon">
                <FileTextOutlined />
              </div>
              <h1 className="app-page-title">Manage Document Types</h1>
            </div>
            <p className="app-page-subtitle">
              View and manage document types
            </p>
          </Col>
          <Col>{headerButtons}</Col>
        </Row>
      </div>
      {filterVisible && (
        <div className="app-filter-bar">
          <div className="app-filter-header">
            <div className="app-filter-title">
              <BiFilterAlt style={{ fontSize: 14 }} />
              Filters
            </div>
            <Button
              type="text"
              size="small"
              icon={<CloseOutlined />}
              onClick={() => setFilterVisible(false)}
              title="Close filters"
              aria-label="Close filters"
            />
          </div>
          <div className="table-filter-form">
            <GenericFormComponent {...formProps} />
          </div>
        </div>
      )}

      <ActiveFilterChipsBar
        show={!filterVisible}
        entries={activeFilterEntries}
        onRemove={removeFilter}
        onClearAll={clearAllFilters}
      />

      <div className="app-table-card">
        <DataTableComponent
          pagination={true}
          loading={loadingDocumentType}
          dataSource={documentTypeListData}
          columns={columns}
          onChange={onChangeHandler}
          defaultPage={0}
          currentPage={pagination.page}
          totalRows={pagination?.total}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
          pageSize={pageSize}
        />
      </div>
      <Modal
        title="Confirm Delete"
        open={deleteModalVisible}
        onOk={onDeleteConfirm}
        onCancel={onDeleteCancel}
      >
        <p>Are you sure you want to delete this document type?</p>
      </Modal>
      {createEditOpen && (
        <CreateEditDocumentTypeComponent
          onClose={() => setCreateEditOpen(false)}
          visible={createEditOpen}
          selectedEntity={selectedEntity}
          onSubmit={createEditSubmit}
        />
      )}
      {openLogs && (
        <Drawer open={openLogs} onClose={onCloseLogs} title="Log Details">
          <ActivityLogComponent
            id={selectedEntity?.id || documentTypeIdToDelete}
            record={selectedEntity}
            module="Document Type"
          />
        </Drawer>
      )}
    </div>
  );
};
export default ManageDocumentTypePage;
